import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { clientAPIKeyApi, webhookApi } from '../../services/rest/apiInstances';
import { store as storeGlobal } from '../rootReducer';
import { addNotification } from './systemNotificationSlice';
import { buildClientApiConfig } from '@/helpers/apiRequestConfig';

export const retrieveWebhookData = createAsyncThunk('webhook/retrieveWebhookData', async () => {
    try {
        const options = buildClientApiConfig();
        const data = await webhookApi.retrieveWebhook(options);
        return data.data;
    } catch (error: any) {
        return error.data;
    }
});

export const retrieveApiKeysData = createAsyncThunk('apikeys/retrieveApiKeysData', async () => {
    try {
        const options = buildClientApiConfig();
        const data = await clientAPIKeyApi.listClientApiKeys(options);
        return data.data;
    } catch (error: any) {
        return error.data;
    }
});

export const generateApiKey = createAsyncThunk(
    'apikeys/generateApiKey',
    async (description: string) => {
        try {
            const options = buildClientApiConfig();
            const data = await clientAPIKeyApi.createClientApiKey(
                {
                    clientApiKeyRequest: { description: description },
                },
                options,
            );
            storeGlobal.dispatch(
                addNotification({ message: 'Api key was created', status: 'success' }),
            );
            return data.data;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message;
            if (errorMessage) {
                storeGlobal.dispatch(addNotification({ message: errorMessage, status: 'error' }));
            }
            return error.data;
        }
    },
);

export interface IWebhookAndApiKeysState {
    isOpenGenerateApiKeyModal: boolean;
    isOpenWebhookEditModal: boolean;
    isOpenQuickCardModal: boolean;
    isOpenWebhookCreateModal: boolean;
    isWebhookDataPending: boolean;
    webhookData: any;
    webhookErrors: any;
    isApiKeysDataPending: boolean;
    apiKeysData: any;
    apiKeysErrors: any;
}

const initialState: IWebhookAndApiKeysState = {
    isOpenGenerateApiKeyModal: false,
    isOpenWebhookEditModal: false,
    isOpenQuickCardModal: false,
    isOpenWebhookCreateModal: false,
    isWebhookDataPending: false,
    webhookData: undefined,
    webhookErrors: undefined,
    isApiKeysDataPending: false,
    apiKeysData: undefined,
    apiKeysErrors: undefined,
};

export const webhookAndApiKeysSlice = createSlice({
    name: 'webhookAndApiKeys',
    initialState,
    reducers: {
        openGenerateApiKeyModal: (state) => {
            state.isOpenGenerateApiKeyModal = true;
        },
        closeGenerateApiKeyModal: (state) => {
            state.isOpenGenerateApiKeyModal = false;
        },
        openWebhookCreateModal: (state) => {
            state.isOpenWebhookCreateModal = true;
        },
        closeWebhookCreateModal: (state) => {
            state.isOpenWebhookCreateModal = false;
        },
        openWebhookEditModal: (state) => {
            state.isOpenWebhookEditModal = true;
        },
        closeWebhookEditModal: (state) => {
            state.isOpenWebhookEditModal = false;
        },
        setModalOpen: (state, action: PayloadAction<any>) => {
            //@ts-ignore
            state[action.payload.modalName] = action.payload.isOpen;
        },
        removeApiKeyById: (state, action: PayloadAction<any>) => {
            const filteredKeys = state.apiKeysData.filter((key: any) => key.id !== action.payload);
            state.apiKeysData = filteredKeys;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveWebhookData.pending, (state) => {
                state.isWebhookDataPending = true;
                state.webhookData = undefined;
                state.webhookErrors = undefined;
            })
            .addCase(retrieveWebhookData.rejected, (state, action) => {
                state.isWebhookDataPending = false;
                state.webhookErrors = action.error;
            })
            .addCase(retrieveWebhookData.fulfilled, (state, action) => {
                state.isWebhookDataPending = false;
                state.webhookData = action.payload;
            })

            .addCase(retrieveApiKeysData.pending, (state) => {
                state.isApiKeysDataPending = true;
                state.apiKeysData = undefined;
                state.webhookErrors = undefined;
            })
            .addCase(retrieveApiKeysData.rejected, (state, action) => {
                state.isApiKeysDataPending = false;
                state.apiKeysErrors = action.error;
            })
            .addCase(retrieveApiKeysData.fulfilled, (state, action) => {
                state.isApiKeysDataPending = false;
                state.apiKeysData = action.payload;
            })
            .addCase(generateApiKey.pending, (state) => {
                state.isApiKeysDataPending = true;
            })
            .addCase(generateApiKey.rejected, (state, action) => {
                state.isApiKeysDataPending = false;
            })
            .addCase(generateApiKey.fulfilled, (state, action) => {
                state.isApiKeysDataPending = false;

                if (action.payload) {
                    state.apiKeysData = state.apiKeysData
                        ? state.apiKeysData.concat([action.payload])
                        : [action.payload];
                }
                state.isOpenGenerateApiKeyModal = false;
            });
    },
});

export const {
    openGenerateApiKeyModal,
    closeGenerateApiKeyModal,
    openWebhookCreateModal,
    closeWebhookCreateModal,
    openWebhookEditModal,
    closeWebhookEditModal,
    removeApiKeyById,
    setModalOpen,
} = webhookAndApiKeysSlice.actions;

export default webhookAndApiKeysSlice.reducer;
